import { spacing } from '@kijiji/theme'
import dynamic from 'next/dynamic'
import { useSession } from 'next-auth/react'
import { useTranslation } from 'next-i18next'
import { useTheme } from 'styled-components'

import {
  FiltersAccordion,
  FiltersAccordionOrigin,
} from '@/components/srp/filters/FiltersAccordion/FiltersAccordion'
import {
  FilterDrawer,
  FilterDrawerAccordionContainer,
  FilterDrawerFooter,
  FilterDrawerHeader,
  FilterDrawerMainWrapper,
} from '@/components/srp/filters/FiltersDrawer/styled'
import { RESET_FILTERS_INPUT, useGetSearchResultsData, useSearchActions } from '@/hooks/srp'
import { useFilterDrawerState } from '@/hooks/srp/useFilterDrawerState'
import { RemoteParamKeys, useToggle } from '@/lib/firebase/hooks'
import { GA_EVENT } from '@/lib/ga/constants/gaEvent'
import { trackEvent } from '@/lib/ga/utils/tracking'
import { BodyText } from '@/ui/atoms/body-text'
import { Button } from '@/ui/atoms/button'
import { DrawerCloseButton } from '@/ui/atoms/drawer'
import { Spacing } from '@/ui/atoms/spacing'

type FilterDrawerProps = {
  accordionContainerRef?: React.RefObject<HTMLDivElement>
}

const SaveSearchChipButton = dynamic(
  () => import('@/components/srp/save-search-chip').then((mod) => mod.SaveSearchChipButton),
  { ssr: false }
)

/**
 * Props for the FilterDrawer component.
 *
 * @property {React.RefObject<HTMLDivElement>} accordionContainerRef - Optional ref object for the accordion container.
 */
export const FiltersDrawer = ({ accordionContainerRef }: FilterDrawerProps) => {
  const { t } = useTranslation(['srp', 'filters'])
  const { refetchResults } = useSearchActions()
  const { setFilterDrawerState, isFilterDrawerOpen, selectedAccordionItems } =
    useFilterDrawerState()

  const { colors } = useTheme()
  const { data: userData } = useSession()
  const userId = userData?.user?.sub ? parseInt(`${userData.user.sub}`, 10) : null

  const { data, loadingResults } = useGetSearchResultsData()
  const totalListings = data?.pagination.totalCount ?? 0

  const saveSearchWithinFiltersDecision = useToggle(RemoteParamKeys.SAVED_SEARCH_WITHIN_FILTERS)

  const handleClickAccordionItem = (id: string) => {
    let newSelectedAccordionItems = selectedAccordionItems ?? []

    if (newSelectedAccordionItems.includes(id)) {
      newSelectedAccordionItems = newSelectedAccordionItems.filter((item) => item !== id)
    } else {
      newSelectedAccordionItems = [...newSelectedAccordionItems, id]
    }

    setFilterDrawerState({ openItems: newSelectedAccordionItems })
  }

  const handleResetFilters = () => {
    refetchResults(RESET_FILTERS_INPUT)
    trackEvent({ action: GA_EVENT.FilterSelectReset })
  }

  const handleOnCancel = () => {
    setFilterDrawerState({ isOpen: false })
  }

  const handleOnClickCloseButton = () => {
    setFilterDrawerState({ isOpen: false })
    trackEvent({ action: GA_EVENT.FilterModalClose, label: 'modal=filters_drawer' })
  }

  return (
    <FilterDrawer
      closeButtonLabel={t('filters.close_modal_label')}
      isOpen={isFilterDrawerOpen}
      label={t('filters.title')}
      onCancel={handleOnCancel}
      hasBackdrop
      direction="right"
      isCustomCloseButton
      data-testid="filter-drawer"
    >
      <FilterDrawerMainWrapper flexDirection="column">
        {saveSearchWithinFiltersDecision?.enabled && userId && (
          <Spacing mTop={spacing.husky}>
            <SaveSearchChipButton />
          </Spacing>
        )}
        <FilterDrawerHeader>
          <BodyText size="large" weight="medium" color={colors.grey.primary}>
            {t('filters.title')}
          </BodyText>
          <DrawerCloseButton
            onClick={handleOnClickCloseButton}
            aria-label={t('filters.close_modal_label')}
          />
        </FilterDrawerHeader>
        <FilterDrawerAccordionContainer ref={accordionContainerRef}>
          <FiltersAccordion
            onClickAccordionItem={handleClickAccordionItem}
            selectedAccordionItems={selectedAccordionItems}
            origin={FiltersAccordionOrigin.QUICK_FILTER}
          />
        </FilterDrawerAccordionContainer>
        <FilterDrawerFooter>
          <Button variant="tertiary" onClick={handleResetFilters}>
            {t('filters.actions.clear_filters')}
          </Button>

          <Button
            isLoading={loadingResults}
            loadingLabel={`${t('filters.show.results_loading')}`}
            onClick={handleOnCancel}
          >
            {t('filters.show.n_listings', { totalListings })}
          </Button>
        </FilterDrawerFooter>
      </FilterDrawerMainWrapper>
    </FilterDrawer>
  )
}
